import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PasswordField } from '../components/common/PasswordField';
import { ShieldCheck, ArrowLeft, KeyRound, Lock } from 'lucide-react';

export const Login: React.FC = () => {
    // Stage: 'credentials' | '2fa_challenge'
    const [stage, setStage] = useState<'credentials' | '2fa_challenge'>('credentials');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // 2FA Challenge state
    const [tempToken, setTempToken] = useState('');
    const [totpCode, setTotpCode] = useState('');
    const [isBackupCodeMode, setIsBackupCodeMode] = useState(false);

    const { login, verify2FA } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Get redirect path from location state or default to home
    const from = (location.state as any)?.from?.pathname || '/';

    const handleCredentialsSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const result = await login(email, password);

            // If 2FA is required, transition to challenge screen
            if (result && result.requires2FA) {
                setTempToken(result.tempToken || '');
                setStage('2fa_challenge');
                setIsLoading(false);
                return;
            }

            // Direct login success - check role routing
            const currentUser = await import('../services/authService').then(m => m.getCurrentUser());
            if (currentUser?.role === 'rider') {
                navigate('/driver/dashboard', { replace: true });
            } else {
                navigate(from, { replace: true });
            }
        } catch (err: any) {
            console.error('Login failed:', err);
            setError(err.message || 'Failed to login. Please check your credentials.');
        } finally {
            setIsLoading(false);
        }
    };

    const handle2FASubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!totpCode.trim()) {
            setError('Please enter your verification code.');
            return;
        }

        setError('');
        setIsLoading(true);

        try {
            await verify2FA(tempToken, totpCode.trim());

            const currentUser = await import('../services/authService').then(m => m.getCurrentUser());
            if (currentUser?.role === 'rider') {
                navigate('/driver/dashboard', { replace: true });
            } else {
                navigate(from, { replace: true });
            }
        } catch (err: any) {
            console.error('2FA verification failed:', err);
            setError(err.message || 'Invalid verification code. Please check your authenticator app.');
        } finally {
            setIsLoading(false);
        }
    };

    const resetToCredentials = () => {
        setStage('credentials');
        setTempToken('');
        setTotpCode('');
        setIsBackupCodeMode(false);
        setError('');
    };

    return (
        <div className="login-container" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            backgroundColor: '#0f172a',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
        }}>
            <div className="login-card" style={{
                padding: '2.5rem',
                backgroundColor: '#1e293b',
                borderRadius: '12px',
                border: '1px solid #334155',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)',
                width: '100%',
                maxWidth: '420px',
                boxSizing: 'border-box'
            }}>
                {stage === 'credentials' ? (
                    <>
                        <div className="login-header" style={{ marginBottom: '1.75rem', textAlign: 'center' }}>
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '48px',
                                height: '48px',
                                borderRadius: '10px',
                                backgroundColor: '#f59e0b1a',
                                color: '#f59e0b',
                                marginBottom: '0.75rem'
                            }}>
                                <Lock size={24} />
                            </div>
                            <h2 style={{ margin: '0 0 0.5rem', color: '#f8fafc', fontSize: '1.5rem', fontWeight: 700 }}>
                                Admin Cockpit
                            </h2>
                            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.875rem' }}>
                                Sign in to your sovereign operations portal
                            </p>
                        </div>

                        {error && (
                            <div className="error-message" role="alert" style={{
                                padding: '0.75rem 1rem',
                                backgroundColor: '#7f1d1d33',
                                border: '1px solid #ef4444',
                                color: '#fca5a5',
                                borderRadius: '6px',
                                marginBottom: '1.25rem',
                                fontSize: '0.875rem'
                            }}>
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleCredentialsSubmit}>
                            <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                                <label htmlFor="email" style={{
                                    display: 'block',
                                    marginBottom: '0.375rem',
                                    color: '#cbd5e1',
                                    fontSize: '0.875rem',
                                    fontWeight: 500
                                }}>
                                    Email Address
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="admin@glutenfreefactory.pk"
                                    required
                                    disabled={isLoading}
                                    autoFocus
                                    style={{
                                        width: '100%',
                                        padding: '0.625rem 0.75rem',
                                        backgroundColor: '#0f172a',
                                        border: '1px solid #334155',
                                        borderRadius: '6px',
                                        color: '#f8fafc',
                                        fontSize: '0.95rem',
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <PasswordField
                                id="password"
                                label="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••••••"
                                required
                                disabled={isLoading}
                            />

                            <button
                                type="submit"
                                className="login-button"
                                disabled={isLoading}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    backgroundColor: '#f59e0b',
                                    color: '#0f172a',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '0.95rem',
                                    fontWeight: 600,
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    opacity: isLoading ? 0.7 : 1,
                                    transition: 'background-color 0.15s ease',
                                    marginTop: '0.5rem'
                                }}
                            >
                                {isLoading ? 'Authenticating...' : 'Sign In'}
                            </button>
                        </form>
                    </>
                ) : (
                    <>
                        <div className="login-header" style={{ marginBottom: '1.75rem', textAlign: 'center' }}>
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '48px',
                                height: '48px',
                                borderRadius: '10px',
                                backgroundColor: '#10b9811a',
                                color: '#10b981',
                                marginBottom: '0.75rem'
                            }}>
                                <ShieldCheck size={28} />
                            </div>
                            <h2 style={{ margin: '0 0 0.5rem', color: '#f8fafc', fontSize: '1.35rem', fontWeight: 700 }}>
                                Two-Factor Authentication
                            </h2>
                            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem' }}>
                                Enter the code from your authenticator app for <strong style={{ color: '#e2e8f0' }}>{email}</strong>
                            </p>
                        </div>

                        {error && (
                            <div className="error-message" role="alert" style={{
                                padding: '0.75rem 1rem',
                                backgroundColor: '#7f1d1d33',
                                border: '1px solid #ef4444',
                                color: '#fca5a5',
                                borderRadius: '6px',
                                marginBottom: '1.25rem',
                                fontSize: '0.875rem'
                            }}>
                                {error}
                            </div>
                        )}

                        <form onSubmit={handle2FASubmit}>
                            <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                                <label htmlFor="2fa-code" style={{
                                    display: 'block',
                                    marginBottom: '0.375rem',
                                    color: '#cbd5e1',
                                    fontSize: '0.875rem',
                                    fontWeight: 500
                                }}>
                                    {isBackupCodeMode ? 'Emergency Recovery Code' : '6-Digit Authentication Code'}
                                </label>
                                <input
                                    id="2fa-code"
                                    type="text"
                                    value={totpCode}
                                    onChange={(e) => {
                                        const val = e.target.value.toUpperCase();
                                        if (isBackupCodeMode) {
                                            setTotpCode(val.slice(0, 10));
                                        } else {
                                            setTotpCode(val.replace(/\D/g, '').slice(0, 6));
                                        }
                                    }}
                                    placeholder={isBackupCodeMode ? 'XXXX-XXXX' : '123456'}
                                    required
                                    disabled={isLoading}
                                    autoFocus
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        backgroundColor: '#0f172a',
                                        border: '1px solid #334155',
                                        borderRadius: '6px',
                                        color: '#f8fafc',
                                        fontSize: '1.25rem',
                                        textAlign: 'center',
                                        letterSpacing: '0.25rem',
                                        fontFamily: 'monospace',
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={isLoading}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    backgroundColor: '#10b981',
                                    color: '#0f172a',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '0.95rem',
                                    fontWeight: 600,
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    opacity: isLoading ? 0.7 : 1,
                                    transition: 'background-color 0.15s ease'
                                }}
                            >
                                {isLoading ? 'Verifying Code...' : 'Verify & Enter Cockpit'}
                            </button>

                            <div style={{
                                marginTop: '1.25rem',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: '0.85rem'
                            }}>
                                <button
                                    type="button"
                                    onClick={resetToCredentials}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#94a3b8',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.25rem',
                                        padding: 0
                                    }}
                                >
                                    <ArrowLeft size={14} /> Back to Sign In
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsBackupCodeMode(!isBackupCodeMode);
                                        setTotpCode('');
                                        setError('');
                                    }}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: '#f59e0b',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.25rem',
                                        padding: 0
                                    }}
                                >
                                    <KeyRound size={14} />
                                    {isBackupCodeMode ? 'Use Authenticator App' : 'Use Recovery Code'}
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default Login;